// Утилита для автоматического логирования ошибок
// Работает независимо от основного приложения

interface ErrorLog {
  message: string;
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  userAgent: string;
  url: string;
  timestamp: string;
  type: 'error' | 'unhandledrejection' | 'resource' | 'chunk';
  additionalData?: any;
}

interface UserSession {
  phone_number?: string;
}

class ErrorLogger {
  private static instance: ErrorLogger;
  private errorQueue: ErrorLog[] = [];
  private isSending = false;
  private maxQueueSize = 50;
  private userSession: UserSession | null = null;

  private constructor() {
    // this.setupGlobalErrorHandlers();
    // this.setupResourceErrorHandlers();
    // this.setupChunkErrorHandlers();
    // this.tryToGetUserSession();
  }

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  // Попытка получить информацию о пользователе из различных источников
  private tryToGetUserSession() {
    try {
      // Попытка получить из localStorage
      const sessionData = localStorage.getItem('session_data');
      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        if (parsed.userSession) {
          this.userSession = parsed.userSession;
        }
      }

      // Попытка получить из Redux store (если доступен)
      if ((window as any).__REDUX_DEVTOOLS_EXTENSION__) {
        // Если Redux DevTools доступны, можем попробовать получить состояние
        setTimeout(() => {
          this.tryToGetFromReduxStore();
        }, 1000);
      }

      // Попытка получить из глобального объекта приложения
      if ((window as any).okidokiStore) {
        this.userSession = (window as any).okidokiStore.getState()?.userSession || null;
      }
    } catch (error) {
      console.warn('Failed to get user session:', error);
    }
  }

  private tryToGetFromReduxStore() {
    try {
      // Попытка получить store из различных возможных мест
      const possibleStores = [
        (window as any).__REDUX_DEVTOOLS_EXTENSION__?.connect()?.getState(),
        (window as any).store,
        (window as any).appStore,
        (window as any).okidokiStore
      ];

      for (const store of possibleStores) {
        if (store && store.getState) {
          const state = store.getState();
          if (state?.userSession) {
            this.userSession = state.userSession;
            break;
          }
        }
      }
    } catch (error) {
      console.warn('Failed to get from Redux store:', error);
    }
  }

  // Публичный метод для установки информации о пользователе
  public setUserSession(session: UserSession | null) {
    this.userSession = session;
  }

  // Публичный метод для получения информации о пользователе
  public getUserSession(): UserSession | null {
    return this.userSession;
  }

  private setupGlobalErrorHandlers() {
    // Обработчик ошибок JavaScript
    window.addEventListener('error', (event) => {
      this.logError({
        message: event.message,
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        type: 'error',
        additionalData: {
          error: event.error
        }
      });
    });

    // Обработчик необработанных Promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        type: 'unhandledrejection',
        additionalData: {
          reason: event.reason
        }
      });
    });
  }

  private setupResourceErrorHandlers() {
    // Обработчик ошибок загрузки ресурсов
    window.addEventListener('error', (event) => {
      if (event.target && event.target !== window) {
        const target = event.target as HTMLElement;
        this.logError({
          message: `Resource load error: ${target.tagName}`,
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString(),
          type: 'resource',
          additionalData: {
            tagName: target.tagName,
            src: (target as HTMLImageElement | HTMLScriptElement).src,
            href: (target as HTMLLinkElement).href
          }
        });
      }
    }, true);
  }

  private setupChunkErrorHandlers() {
    // Обработчик ошибок загрузки чанков webpack
    const originalWebpackRequire = (window as any).__webpack_require__;
    if (originalWebpackRequire) {
      const originalE = originalWebpackRequire.e;
      originalWebpackRequire.e = function(chunkId: string) {
        return originalE.call(this, chunkId).catch((error: any) => {
          ErrorLogger.getInstance().logError({
            message: `Chunk load error: ${chunkId}`,
            stack: error?.stack,
            userAgent: navigator.userAgent,
            url: window.location.href,
            timestamp: new Date().toISOString(),
            type: 'chunk',
            additionalData: {
              chunkId,
              error: error.message
            }
          });
          throw error;
        });
      };
    }
  }

  private logError(errorLog: ErrorLog) {
    return;
    console.error('ErrorLogger caught error:', errorLog);
    
    // Добавляем в очередь
    this.errorQueue.push(errorLog);
    
    // Ограничиваем размер очереди
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    // Пытаемся отправить ошибки
    this.sendErrors();
  }

  private async sendErrors() {
    if (this.isSending || this.errorQueue.length === 0) {
      return;
    }

    this.isSending = true;

    try {
      console.error('ErrorLogger queue:', this.errorQueue);
      this.errorQueue = [];
    } catch (error) {
      console.error('Failed to process errors:', error);
      // Не очищаем очередь при ошибке, попробуем позже
    } finally {
      this.isSending = false;
    }
  }

  // Публичный метод для принудительной отправки ошибок
  public forceSend() {
    this.sendErrors();
  }

  // Публичный метод для добавления кастомной ошибки
  public addError(message: string, additionalData?: any) {
    this.logError({
      message,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      type: 'error',
      additionalData
    });
  }
}

// Инициализируем логгер при загрузке скрипта
const errorLogger = ErrorLogger.getInstance();

// Экспортируем для использования в других модулях
export default errorLogger;

// Добавляем в window для доступа из консоли браузера
(window as any).errorLogger = errorLogger; 